import { useState, useContext, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { login, register, isAuthenticated } from '../services/auth';
import StonewallLogo from '../components/StonewallLogo';
import EyeToggle from '../components/EyeToggle';
import { RE_PASSWORD_COMPLEXITY, PASSWORD_MIN_LENGTH } from '../utils/passwordValidation';

// ─── Shared helpers ────────────────────────────────────────────────────────────

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

const inputBase = 'mt-1 block w-full px-3 py-2 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white';
const inputClass = (err) => `${inputBase} ${err ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'}`;

const ErrorBanner = ({ message }) => message ? (
  <div className="rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 p-4 mb-6">
    <p className="text-sm text-red-700 dark:text-red-300">{message}</p>
  </div>
) : null;


// ─── Sign In Form ───────────────────────────────────────────────────────────────

const SignInForm = ({ onSuccess }) => {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await login(email, password, rememberMe);
      refreshUser();
      navigate('/');
    } catch (err) {
      if (err.response?.status === 429) {
        const mins = err.response.data?.minutesRemaining;
        setError(mins ? `Too many attempts. Please try again in ${Math.ceil(mins)} minutes.` : 'Too many attempts. Please try again later.');
      } else {
        setError(err.response?.data?.message || 'Invalid email or password.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <ErrorBanner message={error} />
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="signin-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
          <input
            id="signin-email" name="email" type="email" autoComplete="email" required
            className={inputClass(false)}
            placeholder="you@example.com"
            value={email} onChange={(e) => setEmail(e.target.value)} disabled={isLoading}
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <label htmlFor="signin-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Password</label>
            <Link to="/forgot-password" className="text-xs font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400">Forgot password?</Link>
          </div>
          <div className="relative">
            <input
              id="signin-password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" required
              className={inputClass(false)}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(e); }}
              disabled={isLoading}
            />
            <EyeToggle show={showPassword} onToggle={() => setShowPassword(!showPassword)} />
          </div>
        </div>
        <div className="flex items-center">
          <input
            id="remember-me" type="checkbox"
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
            checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} disabled={isLoading}
          />
          <label htmlFor="remember-me" className="ml-2 text-sm text-gray-600 dark:text-gray-400">Remember me</label>
        </div>
        <button
          type="submit" disabled={isLoading}
          className="w-full flex justify-center items-center py-3 px-4 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading && (
            <svg className="animate-spin mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          )}
          {isLoading ? 'Signing in...' : 'Sign in'}
        </button>
      </form>
    </>
  );
};

// ─── Register Form ──────────────────────────────────────────────────────────────

const RegisterForm = () => {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const [formData, setFormData] = useState({
    email: '', confirmEmail: '', password: '', confirmPassword: '',
    firstName: '', lastName: '', companyName: '',
    address: '', city: '', state: '', zipCode: '',
    personalPhone: '', personalCountryCode: '+1',
    jobTitle: '', businessPhone: '', businessCountryCode: '+1',
    businessSize: '', industry: '', address2: '',
  });

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '').substring(0, 10);
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (match) {
      let f = '';
      if (match[1]) f += `(${match[1]}`;
      if (match[1].length === 3) f += ') ';
      if (match[2]) f += match[2];
      if (match[2].length === 3) f += '-';
      if (match[3]) f += match[3];
      return f;
    }
    return value;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newValue = (name === 'personalPhone' || name === 'businessPhone') ? formatPhoneNumber(value) : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));
    if (validationErrors[name]) setValidationErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errors = {};
    const mainErrors = [];
    if (!formData.email) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Email is invalid';
    if (!formData.confirmEmail) errors.confirmEmail = 'Please confirm your email';
    else if (formData.confirmEmail !== formData.email) { mainErrors.push('Email addresses do not match'); errors.confirmEmail = true; }
    if (!formData.password) errors.password = 'Password is required';
    else if (formData.password.length < PASSWORD_MIN_LENGTH) { mainErrors.push('Password must be at least 8 characters'); errors.password = true; }
    else if (!RE_PASSWORD_COMPLEXITY.test(formData.password)) { mainErrors.push('Password must include uppercase, lowercase, number and special character (@$!%*?&-)'); errors.password = true; }
    if (!formData.confirmPassword) errors.confirmPassword = 'Please confirm your password';
    else if (formData.confirmPassword !== formData.password && !errors.password) { mainErrors.push('Passwords do not match'); errors.confirmPassword = true; }
    if (!formData.firstName) errors.firstName = 'First name is required';
    if (!formData.lastName) errors.lastName = 'Last name is required';
    if (!formData.companyName) errors.companyName = 'Company name is required';
    if (!formData.address) errors.address = 'Street address is required';
    if (!formData.city) errors.city = 'City is required';
    if (!formData.state) errors.state = 'State is required';
    if (!formData.zipCode) errors.zipCode = 'ZIP code is required';
    else if (!/^\d{5}(-\d{4})?$/.test(formData.zipCode)) errors.zipCode = 'Enter a valid ZIP code';
    setValidationErrors(errors);
    if (mainErrors.length > 0) setError(mainErrors.join('. '));
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validate()) return;
    setIsLoading(true);
    try {
      await register({
        email: formData.email, password: formData.password, confirmPassword: formData.confirmPassword,
        firstName: formData.firstName, lastName: formData.lastName, companyName: formData.companyName,
        address: formData.address, address2: formData.address2, city: formData.city,
        state: formData.state, zipCode: formData.zipCode,
      });
      sessionStorage.setItem('showVerificationModal', 'true');
      refreshUser();
      navigate('/');
    } catch (err) {
      setError(err.message || (err.errors ? Object.values(err.errors).join(' ') : 'Registration failed. Please check your input.'));
    } finally {
      setIsLoading(false);
    }
  };

  const ic = (field) => inputClass(validationErrors[field]);

  return (
    <>
      <ErrorBanner message={error} />
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">First name</label>
            <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} className={ic('firstName')} placeholder="John" disabled={isLoading} />
            {validationErrors.firstName && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{validationErrors.firstName}</p>}
          </div>
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Last name</label>
            <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} className={ic('lastName')} placeholder="Doe" disabled={isLoading} />
            {validationErrors.lastName && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{validationErrors.lastName}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Company name</label>
          <input type="text" id="companyName" name="companyName" value={formData.companyName} onChange={handleChange} className={ic('companyName')} placeholder="My Auto Shop" disabled={isLoading} />
          {validationErrors.companyName && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{validationErrors.companyName}</p>}
        </div>

        <div>
          <label htmlFor="reg-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email address</label>
          <input type="email" id="reg-email" name="email" value={formData.email} onChange={handleChange} className={ic('email')} placeholder="john@example.com" required disabled={isLoading} />
          {validationErrors.email && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{validationErrors.email}</p>}
        </div>

        <div>
          <label htmlFor="confirmEmail" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Confirm email</label>
          <input type="email" id="confirmEmail" name="confirmEmail" value={formData.confirmEmail} onChange={handleChange} className={ic('confirmEmail')} placeholder="john@example.com" required disabled={isLoading} />
          {validationErrors.confirmEmail && typeof validationErrors.confirmEmail === 'string' && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{validationErrors.confirmEmail}</p>}
        </div>

        <div>
          <label htmlFor="reg-password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Password</label>
          <div className="relative">
            <input type={showPassword ? 'text' : 'password'} id="reg-password" name="password" value={formData.password} onChange={handleChange} className={ic('password')} placeholder="••••••••" required disabled={isLoading} />
            <EyeToggle show={showPassword} onToggle={() => setShowPassword(!showPassword)} />
          </div>
          {validationErrors.password && typeof validationErrors.password === 'string' && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{validationErrors.password}</p>}
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Min 8 characters — uppercase, lowercase, number, special character (@$!%*?&-)</p>
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Confirm password</label>
          <div className="relative">
            <input type={showConfirmPassword ? 'text' : 'password'} id="confirmPassword" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} className={ic('confirmPassword')} placeholder="••••••••" required disabled={isLoading} />
            <EyeToggle show={showConfirmPassword} onToggle={() => setShowConfirmPassword(!showConfirmPassword)} />
          </div>
          {validationErrors.confirmPassword && typeof validationErrors.confirmPassword === 'string' && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{validationErrors.confirmPassword}</p>}
        </div>

        <div>
          <label htmlFor="address" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Street address</label>
          <input type="text" id="address" name="address" value={formData.address} onChange={handleChange} className={ic('address')} placeholder="123 Main Street" disabled={isLoading} />
          {validationErrors.address && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{validationErrors.address}</p>}
        </div>

        <div className="grid grid-cols-5 gap-3">
          <div className="col-span-2">
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 dark:text-gray-300">City</label>
            <input type="text" id="city" name="city" value={formData.city} onChange={handleChange} className={ic('city')} placeholder="New York" disabled={isLoading} />
            {validationErrors.city && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{validationErrors.city}</p>}
          </div>
          <div className="col-span-2">
            <label htmlFor="state" className="block text-sm font-medium text-gray-700 dark:text-gray-300">State</label>
            <select id="state" name="state" value={formData.state} onChange={handleChange} disabled={isLoading} className={ic('state')}>
              <option value="">—</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {validationErrors.state && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{validationErrors.state}</p>}
          </div>
          <div className="col-span-1">
            <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300">ZIP</label>
            <input type="text" id="zipCode" name="zipCode" value={formData.zipCode} onChange={handleChange} className={ic('zipCode')} placeholder="10001" disabled={isLoading} />
            {validationErrors.zipCode && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{validationErrors.zipCode}</p>}
          </div>
        </div>

        <div className="pt-1">
          <button
            type="submit"
            disabled={isLoading || !formData.email || !formData.confirmEmail || !formData.password || !formData.confirmPassword || !formData.firstName || !formData.lastName || !formData.companyName || !formData.address || !formData.city || !formData.state || !formData.zipCode}
            className="w-full flex justify-center py-3 px-4 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Creating account...' : 'Create account'}
          </button>
        </div>
      </form>
    </>
  );
};

// ─── Main AuthPage ──────────────────────────────────────────────────────────────

const AuthPage = ({ defaultTab = 'signin' }) => {
  const { darkMode } = useContext(ThemeContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState(defaultTab);

  // Sync tab with URL
  useEffect(() => {
    const newTab = location.pathname === '/register' ? 'register' : 'signin';
    setTab(newTab);
  }, [location.pathname]);

  useEffect(() => {
    if (isAuthenticated()) navigate('/');
  }, [navigate]);

  const switchTab = (newTab) => {
    navigate(newTab === 'register' ? '/register' : '/login');
  };

  return (
    <div className={`flex-1 flex flex-col items-center justify-start px-6 py-10 bg-white dark:bg-gray-900 overflow-y-auto ${darkMode ? 'dark' : ''}`}>
      <div className="w-full max-w-md">

        {/* Brand header */}
        <div className="text-center mb-8">
          <span className="inline-block text-sm bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 px-4 py-1.5 rounded-lg font-bold uppercase tracking-wide mb-3">Beta</span>
          <Link to="/">
            <p className="text-sm md:text-base tracking-widest uppercase text-blue-600 dark:text-blue-400 font-semibold">Stonewall Invoicing</p>
          </Link>
        </div>

        {/* Heading — above the card */}
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-1 text-center">
          {tab === 'signin' ? 'Welcome back' : 'Get started'}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 text-center">
          {tab === 'signin'
            ? 'Sign in to continue to Stonewall Invoicing'
            : 'Create your account — no credit card required'}
        </p>

        {/* Card */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-sm p-8">

          {/* Tab toggle */}
          <div className="flex rounded-lg p-1 mb-8 gap-1 bg-gray-100 dark:bg-gray-700">
            <button
              onClick={() => switchTab('signin')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-semibold transition-all ${
                tab === 'signin'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
              }`}
            >
              Sign in
            </button>
            <button
              onClick={() => switchTab('register')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-semibold transition-all ${
                tab === 'register'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
              }`}
            >
              Create account
            </button>
          </div>

          {tab === 'signin' ? <SignInForm /> : <RegisterForm />}

        </div>
      </div>
    </div>
  );
};

export default AuthPage;
